import type { Application, Candidate, OrgUser } from '@core/types';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import type { ApplicationStatus } from '@core/types';

export type TimelineEvent = {
  id: string;
  primary: string;
  secondary?: string;
  timeLabel: string;
  /** Sort key — ISO or ms */
  sortKey: number;
};

export type NextActionItem = {
  id: string;
  severity: 'high' | 'medium';
  message: string;
  applicationId: string;
  jobTitle: string;
};

/** Activity column: actor, action, optional diff, relative time (Phase 7 §2.4). */
export type ActivityFeedItem = {
  id: string;
  actorInitial: string;
  actorLabel: string;
  action: string;
  diff?: string;
  timeLabel: string;
  sortKey: number;
};

function resolveMemberLabel(teamMembers: OrgUser[], key: string | null | undefined): string {
  if (!key) return 'Team';
  const m = teamMembers.find((u) => u.keycloak_sub === key || u.id === key);
  return m?.display_name?.trim() || m?.email || 'Team member';
}

/** Unified feed: profile, applications, assignee context, AI — newest first. */
export function buildUnifiedActivityFeed(
  candidate: Candidate,
  applications: Application[],
  teamMembers: OrgUser[]
): ActivityFeedItem[] {
  const items: ActivityFeedItem[] = [];
  const candInitial = initialsFromName(candidate.full_name);

  items.push({
    id: 'prof-upd',
    actorInitial: candInitial,
    actorLabel: candidate.full_name,
    action: 'Profile record updated',
    diff: [candidate.current_title, candidate.current_company].filter(Boolean).join(' · ') || undefined,
    timeLabel: relTime(candidate.updated_at),
    sortKey: new Date(candidate.updated_at).getTime(),
  });

  for (const a of applications) {
    const subKey = new Date(a.submitted_at).getTime();
    items.push({
      id: `${a.id}-sub`,
      actorInitial: candInitial,
      actorLabel: candidate.full_name,
      action: `Submitted · ${a.job?.title ?? 'Application'}`,
      diff: a.client?.name ? `Client: ${a.client.name}` : undefined,
      timeLabel: relTime(a.submitted_at),
      sortKey: subKey,
    });

    const assignee = resolveMemberLabel(teamMembers, a.assigned_to);
    const assignInitial = initialsFromName(assignee);
    const stLabel = PIPELINE_STATUS_LABEL[a.status as ApplicationStatus] ?? a.status;
    const stKey = new Date(a.status_updated_at).getTime();
    items.push({
      id: `${a.id}-stage`,
      actorInitial: assignInitial,
      actorLabel: assignee,
      action: `Pipeline stage · ${stLabel}`,
      diff: a.fitment_score != null && !Number.isNaN(a.fitment_score) ? `Fitment ${Math.round(a.fitment_score)}/100` : undefined,
      timeLabel: relTime(a.status_updated_at),
      sortKey: stKey,
    });
  }

  const st = candidate.async_ai_work_status ?? 'idle';
  if (st !== 'idle' && candidate.async_ai_work_updated_at) {
    items.push({
      id: 'ai-work',
      actorInitial: 'AI',
      actorLabel: 'AI assistant',
      action: `Résumé skills job · ${st}`,
      diff: candidate.async_ai_work_operation ?? undefined,
      timeLabel: relTime(candidate.async_ai_work_updated_at),
      sortKey: new Date(candidate.async_ai_work_updated_at).getTime(),
    });
  }

  items.push({
    id: 'prof-created',
    actorInitial: 'GG',
    actorLabel: 'Organization',
    action: 'Candidate record created',
    diff: `Created by ${resolveMemberLabel(teamMembers, candidate.created_by)}`,
    timeLabel: relTime(candidate.created_at),
    sortKey: new Date(candidate.created_at).getTime(),
  });

  items.sort((a, b) => b.sortKey - a.sortKey);
  const seen = new Set<string>();
  const deduped: ActivityFeedItem[] = [];
  for (const it of items) {
    if (seen.has(it.id)) continue;
    seen.add(it.id);
    deduped.push(it);
  }
  return deduped;
}

const STALE_DAYS = 5;
const MS_DAY = 86400000;
const TERMINAL: ApplicationStatus[] = ['placed', 'rejected', 'closed'];

function initialsFromName(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 1).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

function relTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / MS_DAY);
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days}d ago`;
    if (days < 60) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  } catch {
    return '—';
  }
}

/** Profile rail: latest record touch + optional AI line. */
export function buildProfileTimelineEvents(candidate: Candidate): TimelineEvent[] {
  const events: TimelineEvent[] = [
    {
      id: 'updated',
      primary: 'Profile updated',
      secondary: 'Candidate record',
      timeLabel: relTime(candidate.updated_at),
      sortKey: new Date(candidate.updated_at).getTime(),
    },
  ];
  const st = candidate.async_ai_work_status ?? 'idle';
  if (st !== 'idle' && candidate.async_ai_work_updated_at) {
    events.push({
      id: 'ai',
      primary: `AI skills: ${st}`,
      secondary: candidate.async_ai_work_operation ?? undefined,
      timeLabel: relTime(candidate.async_ai_work_updated_at),
      sortKey: new Date(candidate.async_ai_work_updated_at).getTime(),
    });
  }
  return events.sort((a, b) => b.sortKey - a.sortKey);
}

/** Merge application milestones — newest first. */
export function buildApplicationsMergedTimeline(applications: Application[]): TimelineEvent[] {
  const raw: TimelineEvent[] = [];
  for (const a of applications) {
    const job = a.job?.title ?? '—';
    const client = a.client?.name ?? '—';
    const sub = new Date(a.submitted_at).getTime();
    raw.push({
      id: `${a.id}-sub`,
      primary: `Submitted · ${job}`,
      secondary: client,
      timeLabel: relTime(a.submitted_at),
      sortKey: sub,
    });
    const st = new Date(a.status_updated_at).getTime();
    if (!Number.isNaN(st)) {
      const label = PIPELINE_STATUS_LABEL[a.status as ApplicationStatus] ?? a.status;
      raw.push({
        id: `${a.id}-st`,
        primary: `Stage · ${label}`,
        secondary: job,
        timeLabel: relTime(a.status_updated_at),
        sortKey: st,
      });
    }
  }
  raw.sort((a, b) => b.sortKey - a.sortKey);
  const seen = new Set<string>();
  const out: TimelineEvent[] = [];
  for (const e of raw) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
    if (out.length >= 20) break;
  }
  return out;
}

export function buildDocumentsTimelineEvents(candidate: Candidate): TimelineEvent[] {
  const ev: TimelineEvent[] = [];
  if (candidate.base_resume_url) {
    ev.push({
      id: 'resume',
      primary: 'Résumé on file',
      secondary: `${candidate.parsed_skills?.length ?? 0} parsed skills`,
      timeLabel: relTime(candidate.updated_at),
      sortKey: new Date(candidate.updated_at).getTime(),
    });
  }
  if (candidate.async_ai_work_updated_at && (candidate.parsed_skills?.length ?? 0) > 0) {
    ev.push({
      id: 'parse',
      primary: 'Skills from résumé',
      secondary: `${candidate.parsed_skills!.length} tags`,
      timeLabel: relTime(candidate.async_ai_work_updated_at),
      sortKey: new Date(candidate.async_ai_work_updated_at).getTime(),
    });
  }
  return ev.sort((a, b) => b.sortKey - a.sortKey);
}

export function buildInsightsTimelineEvents(
  applications: Application[],
  latestSubIso: string | null
): TimelineEvent[] {
  const ev: TimelineEvent[] = [];
  if (latestSubIso) {
    ev.push({
      id: 'latest-sub',
      primary: 'Latest submission',
      timeLabel: relTime(latestSubIso),
      sortKey: new Date(latestSubIso).getTime(),
    });
  }
  for (const a of applications.slice(0, 8)) {
    ev.push({
      id: `${a.id}-mix`,
      primary: PIPELINE_STATUS_LABEL[a.status as ApplicationStatus] ?? a.status,
      secondary: a.job?.title ?? '—',
      timeLabel: relTime(a.status_updated_at),
      sortKey: new Date(a.status_updated_at).getTime(),
    });
  }
  return ev.sort((a, b) => b.sortKey - a.sortKey).slice(0, 15);
}

export function buildParametersAuditTimeline(candidate: Candidate): TimelineEvent[] {
  const ev: TimelineEvent[] = [
    {
      id: 'mod',
      primary: 'Record updated',
      secondary: 'Candidate row',
      timeLabel: relTime(candidate.updated_at),
      sortKey: new Date(candidate.updated_at).getTime(),
    },
    {
      id: 'crt',
      primary: 'Record created',
      secondary: 'Candidate row',
      timeLabel: relTime(candidate.created_at),
      sortKey: new Date(candidate.created_at).getTime(),
    },
  ];
  if (candidate.async_ai_work_updated_at) {
    ev.push({
      id: 'aiw',
      primary: 'Async AI work touched',
      secondary: candidate.async_ai_work_status ?? undefined,
      timeLabel: relTime(candidate.async_ai_work_updated_at),
      sortKey: new Date(candidate.async_ai_work_updated_at).getTime(),
    });
  }
  return ev.sort((a, b) => b.sortKey - a.sortKey);
}

/** Per-application heartbeat timeline — submitted → stage changes → placement → lock. */
export function buildSingleApplicationTimeline(app: Application): TimelineEvent[] {
  const ev: TimelineEvent[] = [];

  ev.push({
    id: `${app.id}-sub`,
    primary: 'Submitted',
    secondary: app.job?.title ?? undefined,
    timeLabel: relTime(app.submitted_at),
    sortKey: new Date(app.submitted_at).getTime(),
  });

  const stKey = new Date(app.status_updated_at).getTime();
  const subKey = new Date(app.submitted_at).getTime();
  if (!Number.isNaN(stKey) && Math.abs(stKey - subKey) > 60000) {
    ev.push({
      id: `${app.id}-stage`,
      primary: `Stage → ${PIPELINE_STATUS_LABEL[app.status as ApplicationStatus] ?? app.status}`,
      secondary: app.client?.name ?? undefined,
      timeLabel: relTime(app.status_updated_at),
      sortKey: stKey,
    });
  }

  if (app.placement_date) {
    ev.push({
      id: `${app.id}-placed`,
      primary: 'Placed',
      secondary: app.client?.name ?? undefined,
      timeLabel: relTime(app.placement_date),
      sortKey: new Date(app.placement_date).getTime(),
    });
  }

  const updKey = new Date(app.updated_at).getTime();
  if (Math.abs(updKey - stKey) > 60000 && Math.abs(updKey - subKey) > 60000) {
    ev.push({
      id: `${app.id}-upd`,
      primary: 'Record updated',
      timeLabel: relTime(app.updated_at),
      sortKey: updKey,
    });
  }

  if (app.pipeline_lock_expires_at) {
    ev.push({
      id: `${app.id}-lock`,
      primary: 'Pipeline lock expires',
      secondary: app.pipeline_lock_holder?.display_name ?? undefined,
      timeLabel: relTime(app.pipeline_lock_expires_at),
      sortKey: new Date(app.pipeline_lock_expires_at).getTime(),
    });
  }

  return ev.sort((a, b) => b.sortKey - a.sortKey);
}

/** Rule-based next actions for recruiters (v1: stale pipeline + lock expiring). */
export function buildApplicationsNextActions(applications: Application[]): NextActionItem[] {
  const now = Date.now();
  const staleCut = now - STALE_DAYS * MS_DAY;
  const lockSoon = now + 24 * 3600000;
  const out: NextActionItem[] = [];

  for (const a of applications) {
    const terminal = TERMINAL.includes(a.status as ApplicationStatus);
    const stTime = new Date(a.status_updated_at).getTime();
    if (!terminal && stTime < staleCut) {
      out.push({
        id: `stale-${a.id}`,
        severity: 'high',
        message: `No stage movement in ${STALE_DAYS}+ days — review pipeline`,
        applicationId: a.id,
        jobTitle: a.job?.title ?? 'Application',
      });
    }
    if (a.pipeline_lock_expires_at) {
      const exp = new Date(a.pipeline_lock_expires_at).getTime();
      if (exp <= lockSoon && exp > now - MS_DAY) {
        out.push({
          id: `lock-${a.id}`,
          severity: 'high',
          message: 'Pipeline lock expiring soon — renew or release',
          applicationId: a.id,
          jobTitle: a.job?.title ?? 'Application',
        });
      }
    }
  }
  return out.slice(0, 6);
}
